import {AutoLinkNode, LinkNode} from '@lexical/link';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {mergeRegister} from '@lexical/utils';
import {
  $createParagraphNode,
  $createTextNode,
  COMMAND_PRIORITY_LOW,
  type DOMExportOutputMap,
  INSERT_LINE_BREAK_COMMAND,
  type LexicalEditor,
  type LexicalNode,
  LineBreakNode,
  TabNode,
  TextNode,
} from 'lexical';
import {
  $isBeautifulMentionNode,
  type BeautifulMentionComponentProps,
  createBeautifulMentionNode,
} from 'lexical-beautiful-mentions';
import {
  type FormControl,
  StyledContentEditable,
  SuggestionInput,
  Tooltip,
} from 'platform/components';
import styled from 'styled-components';

import {type ComponentProps, forwardRef, useRef} from 'react';
import {useController} from 'react-hook-form';

import {compose, isNil, isNotNil, omit, prop, sortBy, toLower} from 'ramda';

import type {ListContextParametersApiResponse} from '@dms/api/core';
import i18n from '@dms/i18n';

import {type RequiredTestIdProps, suffixTestId, useOnMount} from 'shared/web';

import type {FinancingEndingEditableEmailFormValues} from '../../utils/FinancingEndingEditableEmailFormSchema';

const MENTION_TRIGGER = '/';
const PLACEHOLDER_SPLIT_REGEX = /(\{\{\s*[A-Za-z0-9_.]+\s*\}\})/g;
const PLACEHOLDER_CODE_REGEX = /^\{\{\s*([A-Za-z0-9_.]+)\s*\}\}$/;
const NON_BREAKING_SPACE_REGEX = /\u00A0/g;

// The same box as a placeholder token in the message editor.
const StyledSubjectContentEditable = styled(StyledContentEditable)`
  .mentions {
    display: inline-block;
    background-color: ${({theme}) => theme.colors.palettes.blue[30][100]};
    border-radius: ${({theme}) => theme.radii.large};
    padding: ${({theme}) => theme.getSize(1)} ${({theme}) => theme.getSize(2)};
  }
`;

/**
 * Keeps the subject on one line. Enter and Shift+Enter end in a line break command, which does
 * nothing here; with the placeholder menu open, the menu takes Enter first and no line break is
 * asked for. A pasted line break is shown as a space. The content editable is the one part of the
 * field rendered inside the editor, so it holds these listeners.
 */
function SubjectContentEditable(props: ComponentProps<typeof StyledSubjectContentEditable>) {
  const [editor] = useLexicalComposerContext();
  const unregisterRef = useRef<VoidFunction | null>(null);

  // The composer keeps one editor for its whole life, so the listeners are registered on mount.
  useOnMount(
    function keepSubjectOnOneLine() {
      unregisterRef.current = mergeRegister(
        editor.registerCommand(INSERT_LINE_BREAK_COMMAND, () => true, COMMAND_PRIORITY_LOW),
        editor.registerNodeTransform(LineBreakNode, (node) => {
          node.replace($createTextNode(' '));
        })
      );
    },
    function releaseSubjectListeners() {
      unregisterRef.current?.();
      unregisterRef.current = null;
    }
  );

  return <StyledSubjectContentEditable {...props} />;
}

type PlaceholderMentionData = {code: string; description: string; literal?: string};

const PlaceholderMentionComponent = forwardRef<
  HTMLSpanElement,
  BeautifulMentionComponentProps<PlaceholderMentionData>
>((props, ref) => (
  <span ref={ref} {...omit(['data', 'value', 'trigger'], props)}>
    <Tooltip label={props.data?.description} placement="top">
      {props.value}
    </Tooltip>
  </span>
));

const PlaceholderMention = createBeautifulMentionNode(PlaceholderMentionComponent);

const toText = (text: string) => ({element: document.createTextNode(text)});
const toSpace = () => toText(' ');
const toTextContent = (_editor: LexicalEditor, node: LexicalNode) => toText(node.getTextContent());

/**
 * The subject is one line of plain text. A placeholder goes out as its code, a link as the text the
 * user wrote, and a line break, a tab or a non-breaking space as a space, so no markup or HTML
 * entity reaches the value.
 */
const exportHtmlNodeTransforms = (nodes: DOMExportOutputMap) => {
  nodes.set(PlaceholderMention[0], (_editor, node) => {
    const data = $isBeautifulMentionNode(node) ? node.getData() : undefined;
    const literal = data?.literal;
    const code = data?.code;
    // A placeholder loaded from the text keeps its bytes; an inserted one is canonical.
    return toText(
      typeof literal === 'string' ? literal : `{{ ${typeof code === 'string' ? code : ''} }}`
    );
  });
  nodes.set(AutoLinkNode, toTextContent);
  nodes.set(LinkNode, toTextContent);
  nodes.set(LineBreakNode, toSpace);
  nodes.set(TabNode, toSpace);
  nodes.set(TextNode, (_editor, node) =>
    toText(node.getTextContent().replace(NON_BREAKING_SPACE_REGEX, ' '))
  );

  return nodes;
};

const splitPlaceholders = (value: string, parameters: ListContextParametersApiResponse) =>
  value
    .split(PLACEHOLDER_SPLIT_REGEX)
    .filter((part) => part.length > 0)
    .map((part) => {
      const code = part.match(PLACEHOLDER_CODE_REGEX)?.[1];
      const parameter = parameters.find((candidate) => candidate.code === code);
      // An unknown placeholder stays literal text; text equal to a label stays text.
      return isNotNil(parameter)
        ? new PlaceholderMention[0](MENTION_TRIGGER, parameter.label, {
            code: parameter.code,
            description: parameter.description,
            literal: part,
          })
        : new TextNode(part);
    });

const createTransformStringToNodes =
  (parameters: ListContextParametersApiResponse) =>
  (value: string | null): LexicalNode[] =>
    // An editor left without any node does not report its next change, a whole pasted subject.
    isNil(value) || value === '' ? [$createParagraphNode()] : splitPlaceholders(value, parameters);

export type FinancingEndingSubjectFieldProps = RequiredTestIdProps & {
  control: FormControl<FinancingEndingEditableEmailFormValues>;
  parameters: ListContextParametersApiResponse;
  isLoadingParameters: boolean;
  isDisabled?: boolean;
};

/**
 * The plain-text subject of the financing-ending e-mail. A known `{{ code }}` placeholder is shown
 * as its label, `/` opens a menu of the placeholders, and the value keeps the code. The editor
 * reads its editability and its labels once, so it is remounted when either changes.
 */
export function FinancingEndingSubjectField(props: FinancingEndingSubjectFieldProps) {
  const {field, fieldState} = useController({control: props.control, name: 'subject'});

  // The editor reports the loaded text back; only a different text is an edit.
  const handleChange = (value: string | null) => {
    const subject = value ?? '';
    if (subject === (field.value ?? '')) {
      return;
    }
    field.onChange(subject);
    props.control.onChange();
  };

  const options = sortBy(
    compose(toLower, prop('value')),
    props.parameters.map((parameter) => ({
      value: parameter.label,
      code: parameter.code,
      description: parameter.description,
    }))
  );
  const editorKey = [
    String(props.isDisabled ?? false),
    ...props.parameters.map((parameter) => `${parameter.code}=${parameter.label}`),
  ].join('|');

  return (
    <SuggestionInput
      key={editorKey}
      name={field.name}
      value={field.value ?? null}
      onChange={handleChange}
      label={i18n.t('general.labels.subject')}
      placeholder={i18n.t('page.messageTemplates.labels.placeholderHint')}
      prefix={MENTION_TRIGGER}
      options={options}
      isLoadingOptions={props.isLoadingParameters}
      CustomMention={PlaceholderMention}
      ContentEditableComponent={SubjectContentEditable}
      exportHtmlNodeTransforms={exportHtmlNodeTransforms}
      transformStringToNodes={createTransformStringToNodes(props.parameters)}
      errorMessage={fieldState.error?.message}
      isDisabled={props.isDisabled}
      isRequired
      data-testid={suffixTestId('subject', props)}
    />
  );
}
